import {
  ERC20,
  ERC20Burnable,
  ERC20Burnable__factory,
  ERC20Capped,
  ERC20Capped__factory,
  ERC20Pausable as ERC20Pausable_Base,
  ERC20Pausable__factory,
  ERC20__factory,
  SignerOrProvider,
  StakingToken,
  StakingToken__factory,
} from "../contracts";
import { AddressLike, BigNumberish } from "ethers";
import { TypedContractMethod } from "../contracts/types/common";

export type ERC20Mintable = ERC20 & {
  mint: TypedContractMethod<
    [to: AddressLike, amount: BigNumberish],
    [void],
    "nonpayable"
  >;
};

export type ERC20Pausable = ERC20Pausable_Base & {
  pause: TypedContractMethod<[], [void], "nonpayable">;
  unpause: TypedContractMethod<[], [void], "nonpayable">;
};

export const buildERC20Contract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): ERC20 => ERC20__factory.connect(token, signerOrProvider);

export const buildERC20BurnableContract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): ERC20Burnable => ERC20Burnable__factory.connect(token, signerOrProvider);

export const buildERC20CappedContract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): ERC20Capped => ERC20Capped__factory.connect(token, signerOrProvider);

export const buildERC20MintableContract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): ERC20Mintable => ERC20__factory.connect(token, signerOrProvider) as ERC20Mintable;

export const buildERC20PausableContract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): ERC20Pausable => ERC20Pausable__factory.connect(
    token,
    signerOrProvider,
  ) as ERC20Pausable;

export const buildStakingTokenContract = (
  token: string,
  signerOrProvider: SignerOrProvider,
): StakingToken => StakingToken__factory.connect(token, signerOrProvider);

export const BalanceOf = async (
  token: string,
  address: AddressLike,
  signerOrProvider: SignerOrProvider,
) => await buildERC20Contract(token, signerOrProvider).balanceOf(address);

export const Approve = async (token: string, spender: AddressLike, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).approve(spender, amount);

export const Transfer = async (token: string, to: AddressLike, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).transfer(to, amount);

export const TransferFrom = async (token: string, from: AddressLike, to: AddressLike, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).transferFrom(from, to, amount);

export const TokenName = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).name();

export const TokenSymbol = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).symbol();

export const TokenDecimals = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).decimals();

export const TotalSupply = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).totalSupply();

export const GetAllowance = async (token: string, owner: AddressLike, spender: AddressLike, signerOrProvider: SignerOrProvider) =>
  await buildERC20Contract(token, signerOrProvider).allowance(owner, spender);

export const Mint = async (token: string, to: AddressLike, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20MintableContract(token, signerOrProvider).mint(to, amount);

export const Burn = async (token: string, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20BurnableContract(token, signerOrProvider).burn(amount);

export const BurnFrom = async (token: string, account: AddressLike, amount: BigNumberish, signerOrProvider: SignerOrProvider) =>
  await buildERC20BurnableContract(token, signerOrProvider).burnFrom(account, amount);

export const GetTokenCap = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20CappedContract(token, signerOrProvider).cap();

export const Pause = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20PausableContract(token, signerOrProvider).pause();

export const Unpause = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20PausableContract(token, signerOrProvider).unpause();

export const IsTokenPaused = async (token: string, signerOrProvider: SignerOrProvider) =>
  await buildERC20PausableContract(token, signerOrProvider).paused();

export const TransferOwnership = async (token: string, newOwner: AddressLike, signerOrProvider: SignerOrProvider) =>
  await buildStakingTokenContract(token, signerOrProvider).transferOwnership(newOwner);

export const ERC20Methods = {
  ERC20BalanceOf: BalanceOf,
  ERC20Approve: Approve,
  ERC20Transfer: Transfer,
  ERC20TransferFrom: TransferFrom,
  ERC20TokenName: TokenName,
  ERC20TokenSymbol: TokenSymbol,
  ERC20TokenDecimals: TokenDecimals,
  ERC20TotalSupply: TotalSupply,
  ERC20GetAllowance: GetAllowance,
};

export const ERC20MintableMethods = {
  ...ERC20Methods,
  ERC20Mint: Mint,
};

export const ERC20BurnableMethods = {
  ...ERC20Methods,
  ERC20Burn: Burn,
  ERC20BurnFrom: BurnFrom,
};

export const ERC20PausableMethods = {
  ...ERC20Methods,
  ERC20Pause: Pause,
  ERC20Unpause: Unpause,
  ERC20IsTokenPaused: IsTokenPaused,
};

export const ERC20CappedMethods = {
  ...ERC20Methods,
  ERC20GetTokenCap: GetTokenCap,
};

export const StakingTokenMethods = {
  ...ERC20Methods,
  ...ERC20MintableMethods,
  ...ERC20BurnableMethods,
  ...ERC20PausableMethods,
  ...ERC20CappedMethods,
  StakingTokenTransferOwnership: TransferOwnership,
};

export default StakingTokenMethods;
